import React from 'react'
import cx from 'classnames'

import './style.sass'
import classNames from 'classnames'

type FormErrors = Record<string, { message?: string }>

const breakErrorTextOnLineBreak = (text: string | undefined) => {
	const Element = text && text.includes('\n') ? 'li' : 'p'
	return (
		text &&
		text.split('\n').map((p, i) => {
			return <Element key={i}>{p}</Element>
		})
	)
}

const ValidationError = ({ errors = {}, name = '' }: { errors?: FormErrors; name?: string }) => {
	const cs = cx('ValidationError', {
		'ValidationError--visible': errors[name] && errors[name].message,
	})

	const error = errors[name] && errors[name].message

	// Text placeholder is being kept as a dummy value in order for validation error to be transitionable
	// Transition can't happen when there is no content to transition to
	return <small className={cs}>{breakErrorTextOnLineBreak(error) || 'Text placeholder'}</small>
}

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
	name?: string
	disabled?: boolean
	label?: any
	errors?: FormErrors
	formElementClasses?: string
	containerclasses?: string
	labelclasses?: string
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
	({ name, disabled, label, errors = {}, formElementClasses, containerclasses, labelclasses, ...props }, ref) => {
		const containerClasses = cx('Checkbox__container', containerclasses)
		return (
			<div className='Checkbox'>
				<span className={containerClasses}>
					<input
						type='checkbox'
						name={name}
						disabled={disabled}
						aria-label={label}
						className='Checkbox__input'
						ref={ref}
						{...props}
					/>
					{label && (
						<label
							className={cx('Checkbox__label', labelclasses)}
							htmlFor={name}
							onClick={props.onChange as unknown as React.MouseEventHandler<HTMLLabelElement> | undefined}>
							{label}
						</label>
					)}
				</span>
				{Object.keys(errors).length > 0 && <ValidationError errors={errors} name={name} />}
			</div>
		)
	},
)

interface SwitchProps extends React.InputHTMLAttributes<HTMLInputElement> {
	name?: string
	disabled?: boolean
	label?: string
	desc?: string
	errors?: FormErrors
	className?: string
}

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
	({ name, disabled, label, desc, errors = {}, className = '', ...props }, ref) => {
		const cs = cx('Switch__input', className)
		return (
			<div className='Switch'>
				<input
					type='checkbox'
					name={name}
					aria-label={label}
					disabled={disabled}
					className={cs}
					ref={ref}
					id={name}
					{...props}
				/>
				{label && (
					<label className='Switch__label' htmlFor={name}>
						{label}
					</label>
				)}
				{desc && <small className='Switch__desc'>{desc}</small>}
				{Object.keys(errors).length > 0 && <ValidationError errors={errors} name={name} />}
			</div>
		)
	},
)

interface RadioProps extends React.InputHTMLAttributes<HTMLInputElement> {
	name?: string
	disabled?: boolean
	label?: React.ReactNode
	errors?: FormErrors
	value?: string
}

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
	({ name, disabled, label, errors = {}, ...props }, ref) => {
		return (
			<div className='Radio'>
				<span className='Radio__container'>
					<input
						type='radio'
						name={name}
						disabled={disabled}
						className='Radio__input'
						ref={ref}
						id={props.value}
						{...props}
					/>
					{label && (
						<label className='Radio__label' htmlFor={props.value}>
							{label}
						</label>
					)}
				</span>
			</div>
		)
	},
)

interface RadioGroupProps {
	name?: string
	label?: string
	errors?: FormErrors
	children?: React.ReactNode
}

export const RadioGroup = ({ name, label, errors = {}, children }: RadioGroupProps) => {
	return (
		<div className='RadioGroup FormElement'>
			{label && (
				<label htmlFor={name} className='FormLabel'>
					{label}
				</label>
			)}
			{children}
			{Object.keys(errors).length > 0 && <ValidationError errors={errors} name={name} />}
		</div>
	)
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	className?: string
	disabled?: boolean
	fit?: boolean
	name?: string
	invalid?: boolean
	label?: React.ReactNode
	standalone?: boolean
	errors?: FormErrors
	parentClassName?: string
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, disabled, fit, name, invalid, label, standalone, errors = {}, parentClassName = '', ...props }, ref) => {
		const cs = cx('Input', className, {
			'Input--disabled': disabled,
			'Input--fit': fit,
			'Input--invalid': invalid || errors[name!],
			'Input--standalone': standalone,
		})

		const parentClass = cx('FormElement', parentClassName, {
			'FormElement--standalone': standalone,
		})
		return (
			<div className={parentClass}>
				{label && (
					<label htmlFor={name} className='FormLabel'>
						{label}
					</label>
				)}
				<input className={cs} aria-label={typeof label === 'string' ? label : name} disabled={disabled} name={name} id={name} ref={ref} {...props} />
				{Object.keys(errors).length > 0 && <ValidationError errors={errors} name={name} />}
			</div>
		)
	},
)

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
	type?: string
	className?: string
	disabled?: boolean
	fit?: boolean
	name?: string
	invalid?: boolean
	label?: React.ReactNode
	children?: React.ReactNode
	errors?: FormErrors
	parentClassName?: string
	standalone?: boolean
	placeholder?: string
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
	(
		{
			type,
			className,
			disabled,
			fit,
			name,
			invalid,
			label,
			children,
			errors = {},
			parentClassName = '',
			standalone,
			...props
		},
		ref,
	) => {
		const cs = cx('Select', className, {
			'Select--disabled': disabled,
			'Select--fit': fit,
			'Select--standalone': standalone,
			'Select--invalid': invalid || errors[name!],
		})

		const parentClass = cx('FormElement', parentClassName, {
			'FormElement--standalone': standalone,
		})
		return (
			<div className={parentClass}>
				{label && (
					<label htmlFor={name} className='FormLabel'>
						{label}
					</label>
				)}
				<select className={cs} disabled={disabled} aria-label={typeof label === 'string' ? label : name} name={name} id={name} ref={ref} {...props}>
					{children}
				</select>
				{Object.keys(errors).length > 0 && <ValidationError errors={errors} name={name} />}
			</div>
		)
	},
)

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	type?: string
	className?: string
	disabled?: boolean
	fit?: boolean
	name?: string
	invalid?: boolean
	label?: React.ReactNode
	children?: React.ReactNode
	errors?: FormErrors
	formElementClasses?: string
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
	({ type, className, disabled, fit, name, invalid, label, children, errors = {}, formElementClasses, ...props }, ref) => {
		const cs = cx('Textarea', className, {
			'Textarea--disabled': disabled,
			'Textarea--fit': fit,
			'Textarea--invalid': invalid || errors[name!],
		})

		return (
			<div className={classNames('FormElement', formElementClasses)}>
				{label && (
					<label htmlFor={name} className='FormLabel'>
						{label}
					</label>
				)}
				<textarea aria-label={typeof label === 'string' ? label : name} className={cs} disabled={disabled} name={name} ref={ref} {...props} id={name} />
				{Object.keys(errors).length > 0 && <ValidationError errors={errors} name={name} />}
			</div>
		)
	},
)
